// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import TerceraLevel1PanelTemplate from '@front/templates/TerceraLevel1PanelTemplate.html';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';
import { Control } from './Control';
import { Level1ItemType } from '@shared/utils/Enums/Level1ItemType';
import { Level1PanelWrapper } from '@shared/commons/UtilsClasses/Level1PanelWrapper';
import { MouseButtons } from '../UtilsClasses/ControlsUtils';
import { DynProperty } from '@shared/commons/DynProperty';
import { Level1CalculatorViewModelHelper } from '@shared/commons/cache/Level1Utils/Level1CalculatorViewModelHelper';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
class Level1PanelItem {
    constructor(type, level1PanelWrapper) {
        this.type = type;
        this.level1PanelWrapper = level1PanelWrapper;
        this.label = level1PanelWrapper.getLevel1ItemLabel(type);
        this.updateValues();
    }
    updateValues() {
        this.value = this.level1PanelWrapper.getLevel1ItemValueStr(this.type);
        this.color = Level1CalculatorViewModelHelper.getDesktopColor(this.level1PanelWrapper.getLevel1ItemColoringType(this.type));
    }
}
export class TerceraLevel1Panel extends Control {
    constructor() {
        super();
        this.menuItemSelectedChanged = (item) => {
            if (item.checked) {
                this.addLevel1Item(item.value);
            }
            else {
                this.removeLevel1Item(item.value);
            }
        };
    }
    getType() {
        return ControlsTypes.TerceraLevel1Panel;
    }
    oncomplete() {
        var _a;
        super.oncomplete();
        const instrument = this.get('instrument');
        const account = this.get('account');
        this.level1PanelWrapper = new Level1PanelWrapper(instrument, account);
        this.observe('instrument', (ins) => {
            this.level1PanelWrapper.setInstrument(ins);
        });
        this.observe('account', (acc) => {
            this.level1PanelWrapper.setAccount(acc);
        });
        const initTypes = (_a = this.initTypes) !== null && _a !== void 0 ? _a : this.get('level1DefaultItems');
        this.setActiveLevel1Items(initTypes);
        Control.Ticker.Subscribe(this.TickAsync, this);
    }
    dispose() {
        Control.Ticker.UnSubscribe(this.TickAsync, this);
        super.dispose();
    }
    TickAsync() {
        const level1ActveItems = this.get('level1ActveItems');
        level1ActveItems.forEach((item) => {
            item.updateValues();
        });
        void this.set('level1ActveItems', [...level1ActveItems]);
    }
    onContextMenu(context) {
        const event = context.original;
        if (event.button === MouseButtons.Right) {
            showContextMenu({ x: event.clientX, y: event.clientY, items: this.getMenuItems() });
        }
    }
    callBack(properties) {
        const dp = DynProperty.getPropertyByName(properties, 'level1ActveTypes');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            if (isNullOrUndefined(this.level1PanelWrapper)) {
                this.initTypes = dp.value;
            }
            else {
                this.setActiveLevel1Items(dp.value);
            }
        }
    }
    properties() {
        const properties = [];
        const level1ActveTypes = this.get('level1ActveItems').map((item) => item.type);
        properties.push(new DynProperty('level1ActveTypes', level1ActveTypes, DynProperty.UNKNOWN, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    setActiveLevel1Items(level1Types) {
        const level1ActveItems = [];
        for (const level1Type of level1Types) {
            if (this.level1PanelWrapper.isLevel1ItemHidden(level1Type)) {
                continue;
            }
            level1ActveItems.push(new Level1PanelItem(level1Type, this.level1PanelWrapper));
        }
        const sortedLevel1ActveItems = [...level1ActveItems];
        sortedLevel1ActveItems.sort((a, b) => a.type - b.type);
        void this.set('level1ActveItems', sortedLevel1ActveItems);
        this.updateColumnCount();
    }
    addLevel1Item(type) {
        const level1ActveItems = this.get('level1ActveItems');
        level1ActveItems.push(new Level1PanelItem(type, this.level1PanelWrapper));
        void this.set('level1ActveItems', level1ActveItems.sort((a, b) => a.type - b.type));
        this.updateColumnCount();
    }
    removeLevel1Item(type) {
        let level1ActveItems = this.get('level1ActveItems');
        level1ActveItems = level1ActveItems.filter((item) => item.type !== type);
        void this.set('level1ActveItems', [...level1ActveItems]);
        this.updateColumnCount();
    }
    isActiveTypesContainType(type) {
        const level1ActveItems = this.get('level1ActveItems');
        for (const item of level1ActveItems) {
            if (item.type === type) {
                return true;
            }
        }
        return false;
    }
    getMenuItems() {
        const menuItems = [];
        const allowedItemTypes = this.get('level1Types');
        for (let i = 0; i < allowedItemTypes.length; i++) {
            const type = allowedItemTypes[i];
            if (this.level1PanelWrapper.isLevel1ItemHidden(type)) {
                continue;
            }
            menuItems.push({
                type: NewContextMenuItemType.Default,
                text: this.level1PanelWrapper.getLevel1ItemLocKey(type),
                value: type,
                canCheck: true,
                checked: this.isActiveTypesContainType(type),
                closeOnClick: false,
                event: (item) => {
                    this.menuItemSelectedChanged(item);
                }
            });
        }
        return menuItems;
    }
    updateColumnCount() {
        const level1ActveItems = this.get('level1ActveItems');
        const rowsCount = this.get('rowsCount');
        const columnsCount = Math.ceil(level1ActveItems.length / rowsCount);
        void this.set({ columnsCount });
    }
}
Control.extendWith(TerceraLevel1Panel, {
    template: TerceraLevel1PanelTemplate,
    data: function () {
        return {
            itemMatrix: null,
            account: null,
            instrument: null,
            rowsCount: 2,
            columnsCount: 2,
            level1Types: [],
            level1ActveItems: [],
            level1DefaultItems: [
                Level1ItemType.Last,
                Level1ItemType.Open,
                Level1ItemType.ChangePercent,
                Level1ItemType.High,
                Level1ItemType.Volume,
                Level1ItemType.Low
            ]
        };
    }
});
